// Security: Import encryption utilities for sensitive data
import { encryptForStorage, decryptFromStorage } from '../utils/security';

const SIDE_BAR_OPEN = "sideBarOpen";
const USER_INFO = "UserInfo";
const AGENT_SAVED_CLIENT_INFO = "agent-saved-client-info";
const SAVED_CREDENTIALS = "SavedCredentials";
const imageProfileBase65 = "imageprofilebase65";
const companylogobase65 = "companylogobase65";

// Security: Keys that contain sensitive data should be encrypted
const SENSITIVE_KEYS = [USER_INFO, AGENT_SAVED_CLIENT_INFO, SAVED_CREDENTIALS];

export function getAgentSavedClientInfo() {
  return _parseLocalStorage(AGENT_SAVED_CLIENT_INFO);
}
export function getSideBarStatus() {
  try {
    const value = localStorage.getItem(SIDE_BAR_OPEN);
    return value ? JSON.parse(value) : false;
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error('[localStorage] Error getting sidebar status:', error);
    }
    return false;
  }
}
export function setSideBarStatus(value) {
  try {
    localStorage.setItem(SIDE_BAR_OPEN, JSON.stringify(value));
    return true;
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error('[localStorage] Error setting sidebar status:', error);
    }
    return false;
  }
}
export function getUserInfo() {
  return _parseLocalStorage(USER_INFO);
}
export function setUserInfo(newUserInfo) {
  if (!newUserInfo || typeof newUserInfo !== 'object') {
    const error = new Error('Invalid user info: must be an object');
    if (process.env.NODE_ENV === "development") {
      console.error('[localStorage] Error in setUserInfo:', error);
    }
    throw error;
  }
  
  try {
    // Security: Encrypt sensitive user info before storing
    const encrypted = encryptForStorage(newUserInfo);
    localStorage.setItem(USER_INFO, encrypted);
    return encrypted;
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error('[localStorage] Error in setUserInfo:', error);
    }
    throw error;
  }
}
export function setUserInfoImage(image) {
  try {
    const userInfo = getUserInfo() || {};
    const updatedInfo = { ...userInfo, [imageProfileBase65]: image };
    // Security: Encrypt before storing
    const encrypted = encryptForStorage(updatedInfo);
    localStorage.setItem(USER_INFO, encrypted);
    return true;
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error('[localStorage] Error setting user image:', error);
    }
    return false;
  }
}
export function setUserLogoImage(image) {
  try {
    const userInfo = getUserInfo() || {};
    const updatedInfo = { ...userInfo, [companylogobase65]: image };
    // Security: Encrypt before storing
    const encrypted = encryptForStorage(updatedInfo);
    localStorage.setItem(USER_INFO, encrypted);
    return true;
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error('[localStorage] Error setting logo image:', error);
    }
    return false;
  }
}

// Security: Parse localStorage with decryption for sensitive keys
const _parseLocalStorage = (lsKey) => {
  try {
    const item = localStorage.getItem(lsKey);
    if (!item) {
      return null;
    }
    
    // Security: Decrypt sensitive data
    if (SENSITIVE_KEYS.includes(lsKey)) {
      const decrypted = decryptFromStorage(item);
      if (!decrypted) {
        // Fallback: try to parse as plain JSON (for backward compatibility)
        try {
          return JSON.parse(item);
        } catch (e) {
          return null;
        }
      }
      return decrypted;
    }
    
    // Non-sensitive data - parse as JSON
    return JSON.parse(item);
  } catch (error) {
    // Try fallback for sensitive keys
    if (SENSITIVE_KEYS.includes(lsKey)) {
      try {
        const item = localStorage.getItem(lsKey);
        if (item) {
          return JSON.parse(item);
        }
      } catch (e) {
        // Ignore fallback error
      }
    }
    return null;
  }
};

// Save login credentials (email and password) - encrypted
export function saveCredentials(email, password) {
  try {
    const credentials = {
      email: email,
      password: password,
      savedAt: Date.now()
    };
    // Security: Encrypt credentials before storing
    const encrypted = encryptForStorage(credentials);
    localStorage.setItem(SAVED_CREDENTIALS, encrypted);
    return true;
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error('[localStorage] Error saving credentials:', error);
    }
    return false;
  }
}

// Get saved login credentials - decrypted
export function getSavedCredentials() {
  try {
    return _parseLocalStorage(SAVED_CREDENTIALS);
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error('[localStorage] Error getting saved credentials:', error);
    }
    return null;
  }
}

// Clear saved login credentials
export function clearSavedCredentials() {
  try {
    localStorage.removeItem(SAVED_CREDENTIALS);
    return true;
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error('[localStorage] Error clearing saved credentials:', error);
    }
    return false;
  }
}
